import * as React from "react";
import { DEFAULT_FLEET_MODE, normalizeFleetMode, type FleetMode } from "@/lib/tms/fleet-mode";

type FleetModeContextValue = {
  fleetMode: FleetMode;
  loading: boolean;
  setFleetMode: (fleetMode: FleetMode) => void;
  refreshFleetMode: () => Promise<void>;
};

const FleetModeContext = React.createContext<FleetModeContextValue>({
  fleetMode: DEFAULT_FLEET_MODE,
  loading: true,
  setFleetMode: () => undefined,
  refreshFleetMode: async () => undefined,
});

export function FleetModeProvider({ children }: { children: React.ReactNode }) {
  const [fleetMode, setFleetMode] = React.useState<FleetMode>(DEFAULT_FLEET_MODE);
  const [loading, setLoading] = React.useState(true);

  const refreshFleetMode = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/tms/settings", { credentials: "include" });
      const json = (await response.json()) as { data?: { fleet_mode?: string } };
      const nextMode = json.data?.fleet_mode;
      setFleetMode(normalizeFleetMode(nextMode));
    } catch {
      setFleetMode(DEFAULT_FLEET_MODE);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void refreshFleetMode();
  }, [refreshFleetMode]);

  return (
    <FleetModeContext.Provider value={{ fleetMode, loading, setFleetMode, refreshFleetMode }}>
      {children}
    </FleetModeContext.Provider>
  );
}

export function useFleetMode(): FleetModeContextValue {
  return React.useContext(FleetModeContext);
}
